import { Select } from "../select";
import { useConfirm } from "../confirm-dialog";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Save, Trash2 } from "lucide-react";
import {
  questionTypeList,
  questionTypeMeta,
  type Question,
  type QuestionContent,
  type QuestionType,
} from "@hey404/quiz";
import { apiFetch } from "../lib/auth";
import { inputClass, labelClass } from "../admin-ui";
import { QuestionContentEditor } from "./question-editors";
import type { Mutate } from "./types";

type QuizRow = {
  id: string;
  lessonId: string;
  passingScore: number;
  maxAttempts: number;
  randomize: boolean;
};

type QuestionRow = {
  id: string;
  type: QuestionType;
  prompt: string;
  content: string;
  explanation: string;
  points: number;
  position: number;
};

const toQuestion = (row: QuestionRow): Question =>
  ({
    id: row.id,
    type: row.type,
    prompt: row.prompt,
    explanation: row.explanation,
    points: row.points,
    position: row.position,
    // The API is the only writer and validates against the same schema, so a
    // parse failure means hand-edited data; fall back to the type's default
    // rather than blanking the editor.
    content: safeParse(row.type, row.content),
  }) as Question;

function safeParse(type: QuestionType, raw: string) {
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? parsed
      : questionTypeMeta[type].defaultContent;
  } catch {
    return questionTypeMeta[type].defaultContent;
  }
}

/**
 * Quiz editor for a lesson: settings plus a list of questions, each addressed
 * individually so ids stay stable across saves and historical attempts keep
 * resolving.
 */
export function QuizBuilder({
  lessonId,
  courseId,
  mutate,
}: {
  lessonId: string;
  courseId: string;
  mutate: Mutate;
}) {
  const [quiz, setQuiz] = useState<QuizRow | null>(null);
  const [askConfirm, confirmDialog] = useConfirm();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<QuestionType>("multiple_choice");

  const load = async () => {
    const result = await apiFetch<{ quiz: QuizRow; questions: QuestionRow[] }>(
      `/api/admin/learning-tools/lessons/${lessonId}/quiz`,
    );
    setQuiz(result.quiz);
    setQuestions(result.questions.map(toQuestion));
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load().catch(() => setLoading(false));
    // Reloading on lesson change is the point; `load` is stable enough here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  if (loading)
    return <p className="text-ink-soft p-4 text-sm">Cargando el quiz…</p>;
  if (!quiz)
    return (
      <p className="border-ink bg-bubble border-chip m-4 p-3 text-sm">
        No pudimos cargar el quiz de esta clase.
      </p>
    );

  const saveSettings = (patch: Partial<QuizRow>) => {
    setQuiz({ ...quiz, ...patch });
    return mutate(
      () =>
        apiFetch(`/api/admin/learning-tools/lessons/${lessonId}/quiz`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      "Quiz guardado.",
      courseId,
    );
  };

  const addQuestion = async (type: QuestionType) => {
    const saved = await mutate(
      () =>
        apiFetch(`/api/admin/learning-tools/lessons/${lessonId}/questions`, {
          method: "POST",
          body: JSON.stringify({
            type,
            prompt: "Nueva pregunta",
            explanation: "",
            points: 1,
            content: questionTypeMeta[type].defaultContent,
          }),
        }),
      "Pregunta agregada.",
      courseId,
    );
    if (saved) await load();
  };

  const saveQuestion = async (question: Question) => {
    await mutate(
      () =>
        apiFetch(`/api/admin/learning-tools/questions/${question.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            type: question.type,
            prompt: question.prompt,
            explanation: question.explanation,
            points: question.points,
            content: question.content,
          }),
        }),
      "Pregunta guardada.",
      courseId,
    );
  };

  const removeQuestion = async (id: string) => {
    const sure = await askConfirm({
      title: "¿Eliminar esta pregunta?",
      message:
        "Se quita del quiz de esta clase. Las demás preguntas siguen igual. Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar pregunta",
      destructive: true,
    });
    if (!sure) return;
    const saved = await mutate(
      () =>
        apiFetch(`/api/admin/learning-tools/questions/${id}`, {
          method: "DELETE",
        }),
      "Pregunta eliminada.",
      courseId,
    );
    if (saved) setQuestions((current) => current.filter((q) => q.id !== id));
  };

  const move = async (index: number, direction: -1 | 1) => {
    const next = [...questions];
    const target = next[index + direction];
    const current = next[index];
    if (!target || !current) return;
    next[index + direction] = current;
    next[index] = target;
    setQuestions(next);
    await mutate(
      () =>
        apiFetch(
          `/api/admin/learning-tools/lessons/${lessonId}/questions/reorder`,
          {
            method: "POST",
            body: JSON.stringify({ ids: next.map((q) => q.id) }),
          },
        ),
      "Orden actualizado.",
      courseId,
    );
  };

  const patch = (id: string, changes: Partial<Question>) =>
    setQuestions((current) =>
      current.map((question) =>
        question.id === id
          ? ({ ...question, ...changes } as Question)
          : question,
      ),
    );

  return (
    <div className="space-y-4 p-4">
      {confirmDialog}
      <div className="grid gap-3 sm:grid-cols-3">
        <label className={labelClass}>
          Puntaje mínimo
          <input
            type="number"
            min="0"
            max="100"
            value={quiz.passingScore}
            onChange={(event) =>
              saveSettings({ passingScore: Number(event.target.value) })
            }
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          Intentos (0 = ilimitados)
          <input
            type="number"
            min="0"
            value={quiz.maxAttempts}
            onChange={(event) =>
              saveSettings({ maxAttempts: Number(event.target.value) })
            }
            className={inputClass}
          />
        </label>
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input
            type="checkbox"
            checked={quiz.randomize}
            onChange={(event) =>
              saveSettings({ randomize: event.target.checked })
            }
          />
          Orden aleatorio
        </label>
      </div>

      {questions.length === 0 && (
        <p className="border-ink text-ink-soft border-chip border-dashed p-3 text-sm">
          Este quiz todavía no tiene preguntas.
        </p>
      )}

      {questions.map((question, index) => (
        <fieldset
          key={question.id}
          className="border-ink bg-bone border-chip p-3"
        >
          <legend className="font-display flex items-center gap-2 px-2 text-sm font-bold">
            {index + 1}. {questionTypeMeta[question.type].label}
          </legend>

          <div className="flex items-start gap-2">
            <input
              value={question.prompt}
              onChange={(event) =>
                patch(question.id, { prompt: event.target.value })
              }
              placeholder="Enunciado"
              className={`${inputClass} mt-0`}
            />
            <button
              type="button"
              aria-label="Subir pregunta"
              disabled={index === 0}
              onClick={() => move(index, -1)}
              className="disabled:opacity-30"
            >
              <ChevronUp size={16} />
            </button>
            <button
              type="button"
              aria-label="Bajar pregunta"
              disabled={index === questions.length - 1}
              onClick={() => move(index, 1)}
              className="disabled:opacity-30"
            >
              <ChevronDown size={16} />
            </button>
            <button
              type="button"
              aria-label="Eliminar pregunta"
              onClick={() => removeQuestion(question.id)}
            >
              <Trash2 size={15} />
            </button>
          </div>

          <p className="text-ink-soft mt-2 font-mono text-xs uppercase">
            {questionTypeMeta[question.type].hint}
            {!questionTypeMeta[question.type].autoGraded &&
              " · no suma al puntaje automático"}
          </p>

          <div className="mt-3">
            <QuestionContentEditor
              question={question}
              onChange={(content: QuestionContent[QuestionType]) =>
                patch(question.id, { content } as Partial<Question>)
              }
            />
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_120px]">
            <label className={labelClass}>
              Explicación (se muestra al corregir)
              <textarea
                rows={2}
                value={question.explanation}
                onChange={(event) =>
                  patch(question.id, { explanation: event.target.value })
                }
                className={inputClass}
              />
            </label>
            <label className={labelClass}>
              Puntos
              <input
                type="number"
                min="0"
                value={question.points}
                onChange={(event) =>
                  patch(question.id, { points: Number(event.target.value) })
                }
                className={inputClass}
              />
            </label>
          </div>

          <button
            type="button"
            onClick={() => saveQuestion(question)}
            className="border-ink bg-acid font-display border-chip mt-3 flex items-center gap-1 px-3 py-2 text-xs font-bold"
          >
            <Save size={13} /> Guardar pregunta
          </button>
        </fieldset>
      ))}

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={adding}
          onChange={(event) => setAdding(event.target.value as QuestionType)}
          className={`${inputClass} mt-0 w-auto`}
        >
          {questionTypeList.map((meta) => (
            <option key={meta.type} value={meta.type}>
              {meta.label}
            </option>
          ))}
        </Select>
        <button
          type="button"
          onClick={() => addQuestion(adding)}
          className="border-ink bg-lemon font-display border-chip flex items-center gap-1 px-3 py-2 text-xs font-bold"
        >
          <Plus size={13} /> Agregar pregunta
        </button>
      </div>
    </div>
  );
}
